<template>
  <CCard class="mt-4">
    <CCardHeader>
      <h5>
        <translate translate-context="email_templates.debug_logs">Debug Email Logs</translate>
      </h5>
    </CCardHeader>
    <CCardBody>
      <!-- Debug Mode Warning -->
      <CAlert v-if="!isDebugModeActive" color="warning" class="mb-3">
        <div class="d-flex align-items-start">
          <CIcon name="cilWarning" size="lg" class="mr-3 flex-shrink-0"/>
          <div>
            <strong>
              <translate translate-context="email_templates.debug_logs">
                Debug Mode is Currently Disabled
              </translate>
            </strong>
            <p class="mb-0 mt-2">
              <translate translate-context="email_templates.debug_logs">
                To start logging new emails, enable Debug Mode in the email template configuration. When debug mode is active, emails will be logged here instead of being sent.
              </translate>
            </p>
          </div>
        </div>
      </CAlert>

      <!-- Filters -->
      <CRow class="mb-3">
        <CCol md="6">
          <CInput
              v-model="search"
              :placeholder="$pgettext('email_templates.debug_logs', 'Search by email...')"
              @input="onSearchInput"
          >
            <template #prepend-content>
              <CIcon name="cilSearch"/>
            </template>
          </CInput>
        </CCol>
        <CCol md="6">
          <CSelect
              v-model="sentStatus"
              :options="sentStatusOptions"
              @change="onFilterChange"
          />
        </CCol>
      </CRow>

      <!-- Action Buttons -->
      <div class="mb-3 d-flex flex-wrap gap-2">
        <CButton
            color="info"
            variant="outline"
            @click="openBulkSendModal"
        >
          <CIcon name="cilEnvelopeClosed" class="mr-2"/>
          <translate translate-context="email_templates.debug_logs">Bulk Send</translate>
        </CButton>
        <CButton
            v-if="selectedCount > 0"
            color="primary"
            @click="replaySelected"
        >
          <CIcon name="cilPaperPlane" class="mr-2"/>
          <translate translate-context="email_templates.debug_logs" :translate-params="{count: selectedCount}">
            Send Selected (%{count})
          </translate>
        </CButton>
        <CButton
            v-if="selectedCount > 0"
            color="danger"
            variant="outline"
            @click="deleteSelected"
        >
          <CIcon name="cilTrash" class="mr-2"/>
          <translate translate-context="email_templates.debug_logs" :translate-params="{count: selectedCount}">
            Delete Selected (%{count})
          </translate>
        </CButton>
        <CButton
            v-if="debugLogs.length > 0"
            color="success"
            variant="outline"
            @click="exportToExcel"
        >
          <CIcon name="cilSpreadsheet" class="mr-2"/>
          <translate translate-context="email_templates.debug_logs">Export</translate>
        </CButton>
      </div>

      <!-- Loading indicator -->
      <div v-if="loading" class="text-center py-4">
        <CSpinner color="primary"/>
        <p class="mt-3 text-muted">
          <translate translate-context="email_templates.debug_logs">
            Loading debug logs...
          </translate>
        </p>
      </div>

      <!-- No logs message -->
      <div v-else-if="debugLogs.length === 0" class="text-center py-4">
        <CIcon name="cilEnvelopeClosed" size="3xl" class="text-muted mb-3"/>
        <p class="text-muted">
          <translate translate-context="email_templates.debug_logs">
            No debug logs found for this email template
          </translate>
        </p>
      </div>

      <!-- Logs table -->
      <div v-else>
        <CDataTable
            :items="debugLogs"
            :fields="logTableFields"
            hover
            clickable-rows
            @row-clicked="handleRowClick"
            class="debug-logs-table mb-3"
        >
          <template #selected-header>
            <input
                type="checkbox"
                :checked="allSelected"
                @change="toggleSelectAll"
                @click.stop
            />
          </template>

          <template #selected="{item}">
            <td class="text-center align-middle">
              <input
                  type="checkbox"
                  :checked="selectedLogs.includes(item.id)"
                  @change="toggleLog(item.id)"
                  @click.stop
              />
            </td>
          </template>

          <template #recipient_email="{item}">
            <td class="align-middle">
              <strong>{{ item.recipient_email }}</strong>
            </td>
          </template>

          <template #created_at="{item}">
            <td class="align-middle">
              {{ formatDate(item.created_at) }}
            </td>
          </template>

          <template #sent_at="{item}">
            <td class="align-middle">
              <span v-if="item.sent_at">{{ formatDate(item.sent_at) }}</span>
              <span v-else class="text-muted">-</span>
            </td>
          </template>

          <template #status="{item}">
            <td class="align-middle">
              <CBadge :color="getStatusColor(item)">
                {{ getStatusLabel(item) }}
              </CBadge>
            </td>
          </template>
        </CDataTable>

        <!-- Pagination -->
        <CPagination
            v-if="totalPages > 1"
            :active-page.sync="currentPage"
            :pages="totalPages"
            align="center"
        />
      </div>

      <!-- Results Alert -->
      <CAlert v-if="sendResults" :color="sendResults.error_count > 0 ? 'warning' : 'success'" class="mt-3" @close="sendResults = null">
        <div class="d-flex align-items-start">
          <CIcon :name="sendResults.error_count > 0 ? 'cilWarning' : 'cilCheckCircle'" size="lg" class="mr-3 flex-shrink-0"/>
          <div class="flex-grow-1">
            <strong>
              <translate translate-context="email_templates.debug_logs">
                Send Results
              </translate>
            </strong>
            <div class="mt-2">
              <p class="mb-1">
                <translate translate-context="email_templates.debug_logs" :translate-params="{count: sendResults.success_count}">
                  Successfully sent: %{count}
                </translate>
              </p>
              <p class="mb-1">
                <translate translate-context="email_templates.debug_logs" :translate-params="{count: sendResults.error_count}">
                  Failed: %{count}
                </translate>
              </p>
              <p class="mb-0">
                <translate translate-context="email_templates.debug_logs" :translate-params="{count: sendResults.total_processed}">
                  Total processed: %{count}
                </translate>
              </p>
            </div>

            <!-- Error details -->
            <div v-if="sendResults.errors && sendResults.errors.length > 0" class="mt-3">
              <strong>
                <translate translate-context="email_templates.debug_logs">
                  Errors:
                </translate>
              </strong>
              <ul class="mb-0 mt-2">
                <li v-for="error in sendResults.errors" :key="error.log_id || error.email">
                  <span v-if="error.log_id">
                    <translate translate-context="email_templates.debug_logs" :translate-params="{id: error.log_id, error: error.error}">
                      Log #%{id}: %{error}
                    </translate>
                  </span>
                  <span v-else>
                    <translate translate-context="email_templates.debug_logs" :translate-params="{email: error.email, error: error.error}">
                      %{email}: %{error}
                    </translate>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </CAlert>
    </CCardBody>

    <!-- Modals -->
    <SendSelectedModal
        ref="sendSelectedModal"
        :selected-count="selectedCount"
        @send="handleSendSelected"
    />
    <BulkSendModal
        ref="bulkSendModal"
        @send="handleBulkSend"
    />
  </CCard>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import SendSelectedModal from './SendSelectedModal.vue'
import BulkSendModal from './BulkSendModal.vue'

export default {
  name: 'DebugLogsSection',

  components: {
    SendSelectedModal,
    BulkSendModal
  },

  props: {
    emailType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      search: '',
      sentStatus: '',
      selectedLogs: [],
      currentPage: 1,
      pageSize: 20,
      searchTimeout: null,
      sendResults: null,
      logTableFields: [
        {
          key: 'selected',
          label: '',
          _style: 'width: 40px;'
        },
        {
          key: 'recipient_email',
          label: this.$pgettext('email_templates.debug_logs', 'Recipient')
        },
        {
          key: 'created_at',
          label: this.$pgettext('email_templates.debug_logs', 'Created At')
        },
        {
          key: 'sent_at',
          label: this.$pgettext('email_templates.debug_logs', 'Sent At')
        },
        {
          key: 'status',
          label: this.$pgettext('email_templates.debug_logs', 'Status'),
          _style: 'width: 120px;'
        }
      ]
    }
  },

  computed: {
    ...mapGetters('emailTemplates', [
      'debugLogs',
      'debugLogsPagination',
      'debugLogsLoading',
      'templates',
      'template'
    ]),

    loading() {
      return this.debugLogsLoading
    },

    totalCount() {
      return this.debugLogsPagination?.count || 0
    },

    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize)
    },

    selectedCount() {
      return this.selectedLogs.length
    },

    allSelected() {
      return this.selectedLogs.length === this.debugLogs.length && this.debugLogs.length > 0
    },

    isDebugModeActive() {
      return this.template?.debug_mode || false
    },

    sentStatusOptions() {
      return [
        {
          value: '',
          label: this.$pgettext('email_templates.debug_logs', 'All Status')
        },
        {
          value: 'sent',
          label: this.$pgettext('email_templates.debug_logs', 'Sent')
        },
        {
          value: 'not_sent',
          label: this.$pgettext('email_templates.debug_logs', 'Not Sent')
        },
        {
          value: 'failed',
          label: this.$pgettext('email_templates.debug_logs', 'Failed')
        }
      ]
    }
  },

  watch: {
    currentPage(newPage) {
      this.loadDebugLogs()
    }
  },

  mounted() {
    this.loadDebugLogs()
  },

  methods: {
    ...mapActions('emailTemplates', [
      'fetchDebugLogs',
      'exportDebugLogs',
      'sendToEmails',
      'replaySelectedDebugLogs',
      'deleteSelectedDebugLogs'
    ]),

    async loadDebugLogs() {
      try {
        await this.fetchDebugLogs({
          emailType: this.emailType,
          page: this.currentPage,
          page_size: this.pageSize,
          search: this.search,
          sent_status: this.sentStatus
        })
      } catch (error) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Failed to load debug logs')
        )
      }
    },

    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.loadDebugLogs()
      }, 500)
    },

    onFilterChange() {
      this.currentPage = 1
      this.loadDebugLogs()
    },

    toggleLog(logId) {
      const index = this.selectedLogs.indexOf(logId)
      if (index > -1) {
        this.selectedLogs.splice(index, 1)
      } else {
        this.selectedLogs.push(logId)
      }
    },

    handleRowClick(item) {
      this.toggleLog(item.id)
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedLogs = []
      } else {
        this.selectedLogs = this.debugLogs.map(log => log.id)
      }
    },

    openBulkSendModal() {
      this.$refs.bulkSendModal.open()
    },

    async handleBulkSend({ emailAddresses, sendOnlyPending }) {
      this.confirm({
        title: this.$pgettext('email_templates.debug_logs', 'Send Bulk Emails'),
        message: this.$pgettext('email_templates.debug_logs', 'Are you sure you want to send emails to %{count} address(es)? The emails will be sent to the REAL recipients listed.')
            .replace('%{count}', emailAddresses.length),
        cb_confirm: async () => {
          try {
            const result = await this.sendToEmails({
              emailType: this.emailType,
              emailAddresses: emailAddresses,
              sendOnlyPending: sendOnlyPending
            })

            this.sendResults = result

            if (result.error_count === 0) {
              await this.notifySuccess(
                  this.$pgettext('email_templates.debug_logs', 'Bulk emails sent successfully')
              )
            }

            await this.loadDebugLogs()
          } catch (error) {
            await this.notifyError(
                this.$pgettext('email_templates.debug_logs', 'Failed to send bulk emails')
            )
          }
        }
      })
    },

    async exportToExcel() {
      try {
        await this.exportDebugLogs(this.emailType)
        await this.notifySuccess(
            this.$pgettext('email_templates.debug_logs', 'Debug logs exported successfully')
        )
      } catch (error) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Failed to export debug logs')
        )
      }
    },

    async replaySelected() {
      if (this.selectedLogs.length === 0) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Please select at least one email to send')
        )
        return
      }

      this.$refs.sendSelectedModal.open()
    },

    async handleSendSelected(recipientAddress) {
      try {
        const result = await this.replaySelectedDebugLogs({
          emailType: this.emailType,
          logIds: this.selectedLogs,
          recipientAddress: recipientAddress
        })

        this.sendResults = result
        this.selectedLogs = []

        if (result.error_count === 0) {
          await this.notifySuccess(
              this.$pgettext('email_templates.debug_logs', 'Selected emails sent successfully')
          )
        }

        await this.loadDebugLogs()
      } catch (error) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Failed to send selected emails')
        )
      }
    },

    async deleteSelected() {
      if (this.selectedLogs.length === 0) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Please select at least one email to delete')
        )
        return
      }

      const message = this.$pgettext('email_templates.debug_logs', 'Are you sure you want to delete %{count} selected email(s)? This action is irreversible.')
          .replace('%{count}', this.selectedLogs.length)

      this.confirm({
        title: this.$pgettext('email_templates.debug_logs', 'Delete Selected Emails'),
        message: message,
        cb_confirm: async () => {
          try {
            const result = await this.deleteSelectedDebugLogs({
              emailType: this.emailType,
              logIds: this.selectedLogs
            })

            await this.notifySuccess(
                this.$pgettext('email_templates.debug_logs', 'Successfully deleted %{count} email(s)')
                    .replace('%{count}', result.deleted_count)
            )

            this.selectedLogs = []
            await this.loadDebugLogs()
          } catch (error) {
            await this.notifyError(
                this.$pgettext('email_templates.debug_logs', 'Failed to delete emails')
            )
          }
        }
      })
    },

    getStatusLabel(log) {
      if (log.sent_at === null) {
        return this.$pgettext('email_templates.debug_logs', 'Pending')
      }
      return log.sent_successfully
          ? this.$pgettext('email_templates.debug_logs', 'Sent')
          : this.$pgettext('email_templates.debug_logs', 'Failed')
    },

    getStatusColor(log) {
      if (log.sent_at === null) return 'secondary'
      return log.sent_successfully ? 'success' : 'danger'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString()
    }
  }
}
</script>

<style scoped>
.debug-logs-table >>> tbody tr {
  cursor: pointer;
}

.debug-logs-table >>> tbody tr:hover {
  background-color: #f0f3f5;
}

.gap-2 {
  gap: 0.5rem;
}
</style>
